/**
 * Pilotage Planner store — the saved plans library plus the one that's currently
 * loaded/active, and (Stage 3) the plan-building interaction state. Kept ON the
 * device (localStorage), like the saved-tracks and range-rings stores, so a plan
 * survives a reload. A plan is the whole bundle (route, bearings, contour
 * interactions, freehand, notes); this store owns the list and the CRUD (new,
 * rename, duplicate, delete, load), the active plan, the plan-building flag, the
 * whole-plan visibility toggle, and the transient tool state the toolbar and the
 * chart taps share.
 *
 * Editing goes through `updateActivePlan`, which applies a change to the active
 * plan, re-stamps its updated time and bounds, and persists — so the map layer,
 * the toolbar, and the list all read one consistent source. The typed mutators
 * below (add a waypoint, move it, add a bearing, place a note…) are thin wrappers
 * over it so the chart and toolbar never touch the plan shape directly.
 */
import { create } from 'zustand';
import {
  emptyPlan,
  duplicatePlan as duplicatePlanDoc,
  planBounds,
  type PilotagePlanDoc,
  type LonLat,
  type PlanClearingBearing,
  type PlanBackBearing,
  type FreehandKind,
} from '@/pilotage/plannerModel';

const KEY = 'acp.pilotagePlanner.v1';

function loadPersisted(): PilotagePlanDoc[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return [];
    const d = JSON.parse(raw) as { plans?: unknown };
    return Array.isArray(d.plans) ? (d.plans as PilotagePlanDoc[]) : [];
  } catch {
    return [];
  }
}

function persist(plans: PilotagePlanDoc[]): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(KEY, JSON.stringify({ plans }));
    }
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

let seq = 1;
/** A fresh id with a prefix, e.g. "wp-labc-3" for a waypoint. */
export function makePlannerId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${seq++}`;
}

/** A readable default plan name from the date. */
export function defaultPlanName(when = new Date()): string {
  const iso = new Date(when.getTime() - when.getTimezoneOffset() * 60000).toISOString();
  return `Pilotage plan ${iso.slice(0, 10)}`;
}

/**
 * Which plan-building tool is armed. The toolbar sets it; MapView's chart taps
 * read it to know what a tap means. null = no tool (taps behave normally).
 */
export type PlanTool =
  | 'route' // tap to add a waypoint to the plan route
  | 'wheelover' // select a waypoint, then tap the landmark it triggers off
  | 'transit' // two taps: back mark then front mark
  | 'clearing' // tap the charted object the boundary is anchored on
  | 'back' // tap the object for a heading / back bearing
  | 'contour' // tap a depth contour → the follow / clearing-depth menu
  | 'freehand' // finger-draw an annotation
  | 'note'; // tap to drop a sticky note

/** A depth contour the user has tapped, held while they choose what to do with it. */
export interface PendingContour {
  depthM?: number;
  /** The tapped contour's path, [lon, lat] points (from the charted DEPCNT line). */
  path: LonLat[];
  /** True once "Follow as route" is chosen and we're collecting the two points. */
  following: boolean;
  /** The first along-contour point tapped while following. */
  followFrom?: LonLat;
}

interface PilotagePlannerState {
  /** Every saved plan (persisted). */
  plans: PilotagePlanDoc[];
  /** The plan currently loaded / active on the chart, or null. */
  activePlanId: string | null;
  /** True while in plan-building mode (the toolbar is up). */
  building: boolean;
  /** Whether the active plan's layer is shown on the main chart. */
  planVisible: boolean;

  /** The armed building tool, or null. */
  activeTool: PlanTool | null;
  /** The waypoint currently selected (for a wheel-over, or just highlighted). */
  selectedWaypointId: string | null;
  /** A transit's back mark captured, awaiting the front mark. */
  pendingTransitBack: { position: LonLat; label?: string } | null;
  /** A tapped depth contour held for the follow / clearing-depth choice. */
  pendingContour: PendingContour | null;
  /** A sticky note open for reading / editing, by id. */
  openNoteId: string | null;

  /** Create a new empty plan, make it active, enter building; returns its id. */
  newPlan: (name?: string) => string;
  renamePlan: (id: string, name: string) => void;
  /** Duplicate a plan with fresh ids; returns the copy's id. */
  duplicatePlan: (id: string) => string | null;
  deletePlan: (id: string) => void;
  /** Load a plan as the active one (visible), not necessarily building. */
  loadPlan: (id: string) => void;
  /** Close the active plan (leaves building mode). */
  closePlan: () => void;
  setBuilding: (b: boolean) => void;
  setPlanVisible: (b: boolean) => void;
  /** Apply a change to the active plan (re-stamps updated time + bounds, persists). */
  updateActivePlan: (mutate: (doc: PilotagePlanDoc) => PilotagePlanDoc) => void;

  // --- Building interaction ---
  setTool: (tool: PlanTool | null) => void;
  selectWaypoint: (id: string | null) => void;
  setPendingTransitBack: (m: { position: LonLat; label?: string } | null) => void;
  setPendingContour: (c: PendingContour | null) => void;
  setOpenNote: (id: string | null) => void;

  // --- Typed plan mutators (all go through updateActivePlan) ---
  addRouteWaypoint: (position: LonLat, name?: string) => void;
  moveRouteWaypoint: (id: string, position: LonLat) => void;
  removeRouteWaypoint: (id: string) => void;
  setBaseSpeed: (kn: number) => void;
  addWheelOver: (waypointId: string, target: LonLat, bearingDeg: number, targetLabel?: string) => void;
  addTransit: (front: LonLat, rear: LonLat, frontLabel?: string, rearLabel?: string) => void;
  addClearingBearing: (cb: Omit<PlanClearingBearing, 'id'>) => void;
  addBackBearing: (bb: Omit<PlanBackBearing, 'id'>) => void;
  addContourFollow: (path: LonLat[], depthM?: number) => void;
  addClearingDepth: (path: LonLat[], shallowSide: 'left' | 'right', depthM?: number) => void;
  addFreehand: (kind: FreehandKind, points: LonLat[]) => void;
  addStickyNote: (position: LonLat, text?: string) => string;
  updateStickyNote: (id: string, text: string) => void;
  removeStickyNote: (id: string) => void;
  /** Add a whole plan doc (e.g. imported from the community stub); returns its new id. */
  importPlan: (doc: PilotagePlanDoc, activate: boolean) => string;
}

export const usePilotagePlannerStore = create<PilotagePlannerState>((set, get) => ({
  plans: loadPersisted(),
  activePlanId: null,
  building: false,
  planVisible: true,

  activeTool: null,
  selectedWaypointId: null,
  pendingTransitBack: null,
  pendingContour: null,
  openNoteId: null,

  newPlan: (name) => {
    const id = makePlannerId('pp');
    const doc = emptyPlan(id, name?.trim() || defaultPlanName(), Date.now());
    const plans = [...get().plans, doc];
    persist(plans);
    set({ plans, activePlanId: id, building: true, planVisible: true, activeTool: 'route', selectedWaypointId: null });
    return id;
  },

  renamePlan: (id, name) => {
    const plans = get().plans.map((p) => (p.id === id ? { ...p, name, updatedMs: Date.now() } : p));
    persist(plans);
    set({ plans });
  },

  duplicatePlan: (id) => {
    const src = get().plans.find((p) => p.id === id);
    if (!src) return null;
    const newId = makePlannerId('pp');
    const copy = duplicatePlanDoc(src, newId, Date.now(), makePlannerId);
    const plans = [...get().plans, copy];
    persist(plans);
    set({ plans });
    return newId;
  },

  deletePlan: (id) => {
    const plans = get().plans.filter((p) => p.id !== id);
    persist(plans);
    const wasActive = get().activePlanId === id;
    set({
      plans,
      activePlanId: wasActive ? null : get().activePlanId,
      building: wasActive ? false : get().building,
      activeTool: wasActive ? null : get().activeTool,
    });
  },

  loadPlan: (id) => {
    if (!get().plans.some((p) => p.id === id)) return;
    set({ activePlanId: id, planVisible: true });
  },

  closePlan: () =>
    set({
      activePlanId: null,
      building: false,
      activeTool: null,
      selectedWaypointId: null,
      pendingTransitBack: null,
      pendingContour: null,
      openNoteId: null,
    }),

  setBuilding: (building) => set(building ? { building } : { building, activeTool: null }),
  setPlanVisible: (planVisible) => set({ planVisible }),

  updateActivePlan: (mutate) => {
    const activeId = get().activePlanId;
    if (!activeId) return;
    const plans = get().plans.map((p) => {
      if (p.id !== activeId) return p;
      const next = mutate(p);
      return { ...next, updatedMs: Date.now(), bounds: planBounds(next) };
    });
    persist(plans);
    set({ plans });
  },

  setTool: (activeTool) =>
    set({ activeTool, pendingTransitBack: null, pendingContour: null }),
  selectWaypoint: (selectedWaypointId) => set({ selectedWaypointId }),
  setPendingTransitBack: (pendingTransitBack) => set({ pendingTransitBack }),
  setPendingContour: (pendingContour) => set({ pendingContour }),
  setOpenNote: (openNoteId) => set({ openNoteId }),

  addRouteWaypoint: (position, name) => {
    get().updateActivePlan((doc) => {
      const n = doc.route.waypoints.length + 1;
      return {
        ...doc,
        route: {
          ...doc.route,
          waypoints: [
            ...doc.route.waypoints,
            { id: makePlannerId('wp'), position: [...position] as LonLat, name: name ?? `WP${n}` },
          ],
        },
      };
    });
  },

  moveRouteWaypoint: (id, position) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      route: {
        ...doc.route,
        waypoints: doc.route.waypoints.map((w) =>
          w.id === id ? { ...w, position: [...position] as LonLat } : w,
        ),
      },
    }));
  },

  removeRouteWaypoint: (id) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      route: { ...doc.route, waypoints: doc.route.waypoints.filter((w) => w.id !== id) },
      // Drop any wheel-over that hung off the removed waypoint.
      wheelOvers: doc.wheelOvers.filter((w) => w.waypointId !== id),
    }));
    if (get().selectedWaypointId === id) set({ selectedWaypointId: null });
  },

  setBaseSpeed: (kn) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      route: { ...doc.route, baseSpeedKn: Math.max(0.1, kn) },
    }));
  },

  addWheelOver: (waypointId, target, bearingDeg, targetLabel) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      wheelOvers: [
        ...doc.wheelOvers,
        { id: makePlannerId('wo'), waypointId, target: [...target] as LonLat, targetLabel, bearingDeg },
      ],
    }));
  },

  addTransit: (front, rear, frontLabel, rearLabel) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      transits: [
        ...doc.transits,
        { id: makePlannerId('tr'), front: [...front] as LonLat, rear: [...rear] as LonLat, frontLabel, rearLabel },
      ],
    }));
  },

  addClearingBearing: (cb) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      clearingBearings: [...doc.clearingBearings, { ...cb, id: makePlannerId('cb'), anchor: [...cb.anchor] as LonLat }],
    }));
  },

  addBackBearing: (bb) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      backBearings: [...doc.backBearings, { ...bb, id: makePlannerId('bb'), anchor: [...bb.anchor] as LonLat }],
    }));
  },

  addContourFollow: (path, depthM) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      contourFollows: [...doc.contourFollows, { id: makePlannerId('cf'), depthM, path: path.map((p) => [...p] as LonLat) }],
    }));
  },

  addClearingDepth: (path, shallowSide, depthM) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      clearingDepths: [
        ...doc.clearingDepths,
        { id: makePlannerId('cd'), depthM, shallowSide, path: path.map((p) => [...p] as LonLat) },
      ],
    }));
  },

  addFreehand: (kind, points) => {
    if (points.length < 2) return;
    get().updateActivePlan((doc) => ({
      ...doc,
      freehand: [...doc.freehand, { id: makePlannerId('fh'), kind, points: points.map((p) => [...p] as LonLat) }],
    }));
  },

  addStickyNote: (position, text = '') => {
    const id = makePlannerId('sn');
    get().updateActivePlan((doc) => ({
      ...doc,
      stickyNotes: [...doc.stickyNotes, { id, position: [...position] as LonLat, text }],
    }));
    return id;
  },

  updateStickyNote: (id, text) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      stickyNotes: doc.stickyNotes.map((n) => (n.id === id ? { ...n, text } : n)),
    }));
  },

  removeStickyNote: (id) => {
    get().updateActivePlan((doc) => ({
      ...doc,
      stickyNotes: doc.stickyNotes.filter((n) => n.id !== id),
    }));
    if (get().openNoteId === id) set({ openNoteId: null });
  },

  importPlan: (doc, activate) => {
    const id = makePlannerId('pp');
    const stamped: PilotagePlanDoc = { ...doc, id, createdMs: Date.now(), updatedMs: Date.now() };
    const plans = [...get().plans, stamped];
    persist(plans);
    if (activate) set({ plans, activePlanId: id, planVisible: true, building: false, activeTool: null });
    else set({ plans });
    return id;
  },
}));

/** The active plan document, or null. A selector so components stay tidy. */
export function selectActivePlan(
  state: Pick<PilotagePlannerState, 'plans' | 'activePlanId'>,
): PilotagePlanDoc | null {
  if (!state.activePlanId) return null;
  return state.plans.find((p) => p.id === state.activePlanId) ?? null;
}
